import { useState } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { Link } from 'react-router-dom';

interface KycRequest {
  id: number;
  requestNumber: string;
  customerName: string;
  customerEmail: string;
  status: string;
  riskScore: number;
  riskLevel: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

import CreateKycModal from './components/CreateKycModal';

export default function KycRequests() {
  const { data: requests, loading, refetch } = useApiQuery<KycRequest[]>({ endpoint: '/kyc' });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const filtered = requests?.filter((r) => {
    const matchesSearch = (r.customerName || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.requestNumber || '').toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || r.status === filter;
    return matchesSearch && matchesFilter;
  });

  const statusColors: Record<string, string> = {
    draft: 'bg-stone-100 text-stone-600',
    submitted: 'bg-sky-50 text-sky-700',
    'under review': 'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">KYC Requests</h1>
          <p className="text-stone-500 text-sm mt-1">Track and manage all identity verification requests.</p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" />
          New KYC Request
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by request number or customer..."
              className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'submitted', 'under review', 'approved', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize whitespace-nowrap ${
                  filter === s
                    ? 'bg-brand-600 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Request</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Risk</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered?.map((req) => (
                  <tr key={req.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                          <i className="ri-file-list-3-line text-stone-400 text-sm" />
                        </div>
                        <div>
                          <p className="text-stone-900 text-sm font-medium">{req.requestNumber}</p>
                          <p className="text-stone-400 text-xs">ID: {req.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-stone-900 text-sm font-medium">{req.customerName}</p>
                      <p className="text-stone-400 text-xs">{req.customerEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[req.status] || 'bg-stone-100 text-stone-700'}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${req.riskScore > 70 ? 'bg-red-500' : req.riskScore > 40 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${req.riskScore}%` }}
                          />
                        </div>
                        <span className="text-stone-600 text-xs font-medium">{req.riskScore}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-sm">
                      {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : 'Draft'}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/kyc/${req.id}`} className="text-brand-600 text-sm font-medium hover:text-brand-700">
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered?.length === 0 && (
              <div className="p-8 text-center">
                <i className="ri-file-list-3-line text-stone-300 text-3xl mb-2" />
                <p className="text-stone-400 text-sm">No KYC requests found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <CreateKycModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={refetch} />
    </div>
  );
}